import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import type { TSpaceBase } from '@onesource/schemas';
import { ModuleCard } from './module-card';
import { ModulePreview } from './module-preview';

interface ModulesTabContentProps {
  subSpaces: TSpaceBase[]; // CONTENT type sub-spaces (modules)
  parentSpace: TSpaceBase;
}

/**
 * Modules Tab Content
 * Split view with module list on the left and preview on the right
 * Based on Figma design
 */
export const ModulesTabContent = ({ subSpaces, parentSpace }: ModulesTabContentProps) => {
  const [selectedModule, setSelectedModule] = useState<TSpaceBase | null>(null);

  // Auto-select first module on mount or when subSpaces change
  useEffect(() => {
    if (subSpaces.length > 0 && !selectedModule) {
      setSelectedModule(subSpaces[0]);
    }
  }, [subSpaces, selectedModule]);

  if (subSpaces.length === 0) {
    return <Alert severity="info">No modules available in this space.</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', gap: 3, minHeight: 500 }}>
      {/* Left Panel - Module List */}
      <Box
        sx={{
          width: 370,
          flexShrink: 0,
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 250px)',
        }}
      >
        {subSpaces.map((module, index) => (
          <ModuleCard
            key={module.id}
            module={module}
            parentSpaceId={parentSpace.id}
            index={index + 1}
            isSelected={selectedModule?.id === module.id}
            onClick={() => setSelectedModule(module)}
          />
        ))}
      </Box>

      {/* Divider */}
      <Box
        sx={{
          width: '1px',
          bgcolor: 'divider',
          flexShrink: 0,
        }}
      />

      {/* Right Panel - Module Preview */}
      <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 250px)' }}>
        {selectedModule ? (
          <ModulePreview module={selectedModule} parentSpace={parentSpace} />
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
            Select a module to preview
          </Typography>
        )}
      </Box>
    </Box>
  );
};
